import { ChangeDetectionStrategy, Component, inject, input, output } from "@angular/core";

import { EMPTY, Observable } from "rxjs";

import { FolderItemModel, FolderModel } from "@documentation/models/folder.model";
import { DocumentationRoutesConstants } from "@documentation/routes/documentation.routes.constants";
import { FolderHttpService } from "@documentation/services/folder-http.service";
import { BaseDocumentListContextMenuComponent } from "@features/documentation/components/sidebar/tabs/documents-list/document-tree/base/base-context-menu.component";
import {
  NgbDropdown,
  NgbDropdownButtonItem,
  NgbDropdownItem,
  NgbDropdownMenu,
  NgbDropdownToggle
} from "@ng-bootstrap/ng-bootstrap";
import { CustomIconComponent } from "@shared/components/custom-icon/custom-icon.component";
import { ToggleComponent } from "@shared/components/toggle/toggle.component";

@Component({
  selector: "component-folder-context-menu",
  imports: [
    CustomIconComponent,
    NgbDropdown,
    NgbDropdownButtonItem,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
    ToggleComponent
  ],
  templateUrl: "./folder-context-menu.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FolderContextMenuComponent extends BaseDocumentListContextMenuComponent<FolderModel> {
  readonly isCreateDisabled = input.required<boolean>();
  readonly folderDelete = output<FolderModel>();
  readonly documentCreate = output<FolderModel>();

  protected override readonly entityType = FolderModel;

  protected override readonly publicSuccessMessage = "Folder is now public";
  protected override readonly privateSuccessMessage = "Folder is now private";

  private readonly _folderHttpService = inject(FolderHttpService);

  onRenameClick(event: Event): void {
    this.handleOnRenameClick(event, this.entity().name, "Rename Folder", "Folder Name");
  }

  protected override handleRename(name: string): void {
    this._folderHttpService.updateFolderName(this.entity().id, name).subscribe({
      next: () => {
        this.entityUpdated.emit(this.getUpdatedModel({ name }));
      },
      error: (err) => {
        this._logger.error("Failed to update document title", err);
        this._toastService.error("Failed to update document title");
      }
    });
  }

  protected override async togglePublicRequest(isPublic: boolean): Promise<Observable<FolderItemModel>> {
    const entity = this.entity();
    const content = entity?.content ?? [];

    // Case 1: Folder becoming private AND it has items → show modal about public items
    if (!isPublic && content.length) {
      const res = (await this._modalService.confirmChoices(
        "Folder contains public items",
        "This folder contains public items. How should their visibility be updated?",
        [
          { id: "CANCEL", label: "Cancel", btnClass: "btn-outline-secondary" },
          { id: "MAKE_PRIVATE", label: "Make private", btnClass: "btn-primary" },
          { id: "LEAVE_PUBLIC", label: "Leave public", btnClass: "btn-primary" }
        ]
      )) as any;

      if (["MAKE_PRIVATE", "LEAVE_PUBLIC"].includes(res)) return this._folderHttpService.setPublic(entity.id, isPublic, res);

      // User canceled
      this.publicStatusToggle()?.writeValue(!isPublic);
      return EMPTY;
    }

    // Case 2: Folder becoming public AND it contains at least one private child → show modal
    if (isPublic && content.some((item) => item.isPublic === false)) {
      const confirm = await this._modalService.confirm(
        "Make all items public?",
        "Folder contains private items. They will become public."
      );

      if (!confirm) {
        // user canceled — revert toggle
        this.publicStatusToggle()?.writeValue(!isPublic);
        return EMPTY;
      }
    }

    // Case 3: Default simple toggle
    return this._folderHttpService.setPublic(entity.id, isPublic);
  }

  protected override handlePublicStatusChange(): void {
    this.treeUpdated.emit();
  }

  // Note: this doesn't work with nested folders
  protected override getPublicEntityUrl(id: string): string {
    return DocumentationRoutesConstants.publicFolder(id);
  }

  protected async onDeleteClick(event: Event): Promise<void> {
    this.onMenuClick(event);

    const confirmed = await this._modalService.confirm(
      `Delete Folder ${this.entity().name}`,
      "This action is permanent and cannot be undone.",
      "Delete"
    );

    if (confirmed) {
      this.folderDelete.emit(this.entity());
    }
  }

  protected onCreateDocument(event: Event): void {
    this.onMenuClick(event);

    this.documentCreate.emit(this.entity());
  }
}
